/**
 * OPERATIONAL LINEAGE GRAPH PANEL
 *
 * Read-only lineage graph inspection surface for the Operational Bundle Viewer.
 * Primary view: deterministic SVG graph canvas with zoom/pan/legend/minimap.
 * Secondary view: compact node inspector. Tertiary: structured edge table.
 */

import React, { useState } from 'react';
import type {
  OperationalLineageGraph,
  LineageNode,
  LineageVerificationResult,
} from '../operationalLineageTypes.js';
import { OperationalLineageSummaryCard } from './OperationalLineageSummaryCard.js';
import { OperationalLineageNodeInspector } from './OperationalLineageNodeInspector.js';
import { OperationalLineageGraphCanvas } from './OperationalLineageGraphCanvas.js';
import { NEUTRAL, TYPOGRAPHY, SPACING } from '../../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface Props {
  graph: OperationalLineageGraph | null;
  verification: LineageVerificationResult | null;
}

export const OperationalLineageGraphPanel: React.FC<Props> = ({ graph, verification }) => {
  const [selectedNode, setSelectedNode] = useState<LineageNode | null>(null);

  if (!graph) {
    return (
      <div data-testid="lineage-panel-empty">
        <p style={{ color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No lineage graph available.</p>
      </div>
    );
  }

  return (
    <div data-testid="lineage-graph-panel" style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sp4, height: '100%' }}>
      <OperationalLineageSummaryCard graph={graph} verification={verification} />

      {/* Graph canvas + inspector side-by-side */}
      <div style={{ display: 'flex', gap: SPACING.sp4, flex: 1, minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <OperationalLineageGraphCanvas
            graph={graph}
            selectedNodeId={selectedNode?.id ?? null}
            onSelectNode={setSelectedNode}
          />
        </div>
        <div
          style={{
            width: 240,
            minWidth: 240,
            overflow: 'auto',
            backgroundColor: NEUTRAL.bg200,
            border: `1px solid ${NEUTRAL.borderSubtle}`,
            borderRadius: 2,
            padding: SPACING.sp3,
          }}
        >
          <OperationalLineageNodeInspector node={selectedNode} />
        </div>
      </div>

      {/* Warnings */}
      {verification && verification.warnings.length > 0 && (
        <div
          style={{
            backgroundColor: '#151106',
            border: `1px solid #5a3a00`,
            borderRadius: 2,
            padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
            fontSize: TYPOGRAPHY.micro.size,
            color: '#c89010',
          }}
          data-testid="lineage-warnings"
        >
          {verification.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      )}

      {/* Edge detail table — collapsed by default, minimal */}
      <details style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
        <summary style={{ cursor: 'pointer', color: NEUTRAL.text400, fontWeight: TYPOGRAPHY.label.weight }}>
          Edge details ({graph.edgeCount})
        </summary>
        <table style={{ width: '100%', fontSize: TYPOGRAPHY.micro.size, borderCollapse: 'collapse', marginTop: SPACING.sp2 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${NEUTRAL.border}` }}>
              <th style={{ textAlign: 'left', color: NEUTRAL.text300, fontWeight: TYPOGRAPHY.label.weight }}>Source</th>
              <th style={{ textAlign: 'left', color: NEUTRAL.text300, fontWeight: TYPOGRAPHY.label.weight }}>Type</th>
              <th style={{ textAlign: 'left', color: NEUTRAL.text300, fontWeight: TYPOGRAPHY.label.weight }}>Target</th>
            </tr>
          </thead>
          <tbody>
            {graph.edges.map((edge) => (
              <tr key={edge.id} data-testid={`lineage-edge-${edge.type}`} style={{ borderBottom: `1px solid ${NEUTRAL.borderSubtle}` }}>
                <td style={{ color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family }}>{edge.source.slice(0, 20)}…</td>
                <td style={{ color: NEUTRAL.text300 }}>{edge.type}</td>
                <td style={{ color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family }}>{edge.target.slice(0, 20)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};
